<script setup>
import { useForm } from '@inertiajs/vue3'
import { computed } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import CustomerChannels from '@/Components/CustomerChannels.vue'
import PhoneInput from '@/Components/UI/PhoneInput.vue'
import { DEFAULT_PLATFORMS } from '@/composables/useChannelConfig.js'
import { useI18n } from 'vue-i18n'

defineOptions({ layout: AppLayout })

const props = defineProps({
  host:     { type: Object, required: true },
  channels: { type: Array,  default: () => [] },
  errors:   { type: Object, default: () => ({}) },
  defaults: { type: Object, default: () => ({}) }
})

const { t } = useI18n()
const isEdit = computed(() => !!props.host.id)

function seedChannels(channels) {
  const result = [...channels]
  for (const platform of DEFAULT_PLATFORMS) {
    if (!result.find(c => c.platform === platform)) {
      result.push({ platform, handle: '', custom_label: '', position: DEFAULT_PLATFORMS.indexOf(platform) })
    }
  }
  return result
}

const form = useForm({
  name:                        props.host.name ?? '',
  first_name:                  props.host.first_name ?? '',
  last_name:                   props.host.last_name ?? '',
  contact_phone:               props.host.contact_phone ?? '',
  contact_email:               props.host.contact_email ?? '',
  notes:                       props.host.notes ?? '',
  contact_channels_attributes: seedChannels(props.channels),
  return_to:                   props.defaults?.return_to ?? '',
  link_conversation:           props.defaults?.link_conversation ?? ''
})

function submit() {
  form.transform((data) => {
    const { return_to, link_conversation, ...hostData } = data
    return {
      property_host: {
        ...hostData,
        contact_channels_attributes: hostData.contact_channels_attributes
          .filter(c => c.handle !== '' || c._destroy)
          .map((c, i) => ({ ...c, position: i }))
      },
      return_to,
      link_conversation
    }
  })
  const opts = { preserveScroll: true }
  if (isEdit.value) {
    form.patch(`/property_hosts/${props.host.id}`, opts)
  } else {
    form.post('/property_hosts', opts)
  }
}

const err = (k) => form.errors[k] || (props.errors[k] && props.errors[k][0])
const labelCls = 'block text-sm font-medium text-[color:var(--inkSoft)] mb-1'
</script>

<template>
  <PageShell
    narrow
    :title="isEdit ? t('property_hosts.form.title_edit') : t('property_hosts.form.title_new')"
    back-href="/property_hosts"
    :back-label="t('common.back')"
  >
    <form id="property-host-form" class="space-y-4" @submit.prevent="submit">
      <!-- Business name -->
      <div>
        <label :class="labelCls">{{ t('property_hosts.form.fields.name') }} *</label>
        <UiInput
          v-model="form.name"
          required
          :error="err('name')"
        />
      </div>

      <!-- Contact person -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label :class="labelCls">{{ t('property_hosts.form.fields.first_name') }}</label>
          <UiInput v-model="form.first_name" :error="err('first_name')" />
        </div>
        <div>
          <label :class="labelCls">{{ t('property_hosts.form.fields.last_name') }}</label>
          <UiInput v-model="form.last_name" :error="err('last_name')" />
        </div>
      </div>

      <!-- Contact info -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label :class="labelCls">{{ t('property_hosts.form.fields.contact_phone') }}</label>
          <PhoneInput v-model="form.contact_phone" />
        </div>
        <div>
          <label :class="labelCls">{{ t('property_hosts.form.fields.contact_email') }}</label>
          <UiInput v-model="form.contact_email" type="email" :error="err('contact_email')" />
        </div>
      </div>

      <!-- Notes -->
      <div>
        <label :class="labelCls">{{ t('property_hosts.form.fields.notes') }}</label>
        <UiTextarea v-model="form.notes" :rows="3" :error="err('notes')" />
      </div>

      <!-- Channels -->
      <div class="border-t border-[color:var(--line)] pt-4">
        <label :class="labelCls">{{ t('property_hosts.form.fields.channels') }}</label>
        <CustomerChannels v-model="form.contact_channels_attributes" />
      </div>
    </form>

    <StickyActionBar>
      <UiButton
        type="submit"
        form="property-host-form"
        variant="primary"
        :loading="form.processing"
      >
        {{ isEdit ? t('property_hosts.form.submit_edit') : t('property_hosts.form.submit_new') }}
      </UiButton>
      <UiButton
        variant="ghost"
        @click="$inertia.visit('/property_hosts')"
      >
        {{ t('property_hosts.form.cancel') }}
      </UiButton>
    </StickyActionBar>
  </PageShell>
</template>
